import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useComfort } from "../ComfortContext";
import { getDictionary } from "../../i18n/dictionary";
import {
  generateLinkCode,
  normalizeLinkCode,
  isValidLinkCode,
  formatLinkCode,
  claimTransition,
} from "../../domain/linkCode";
import type { ClaimState } from "../../domain/linkCode";
import { getPortalData, shouldUseSupabase, type ServerChild } from "../../data";
import { PortalWriteError } from "../../data/supabaseAdapter";
import type { ServiceOutletContext } from "./ServiceSettings";

type LinkTab = "have" | "share";

/** Calm, claims-clean copy for a claim failure, mapped on the RPC's SQLSTATE code. */
function claimErrorMessage(e: unknown): string {
  const code = e instanceof PortalWriteError ? e.code : undefined;
  if (code === "42501") return "You'll need to be signed in to link a child.";
  if (code === "P0002")
    return "That code doesn't look right — check it and try again.";
  if (code === "P0001")
    return "That code can't be used — it may have expired or already been used.";
  return "That didn't go through. Please try again in a moment.";
}

// ---- Tab bar -----------------------------------------------------------------

function TabBar({
  tab,
  haveLabel,
  shareLabel,
  onChange,
}: {
  tab: LinkTab;
  haveLabel: string;
  shareLabel: string;
  onChange: (t: LinkTab) => void;
}) {
  const base: React.CSSProperties = {
    flex: 1,
    minHeight: 44,
    border: 0,
    fontFamily: "var(--font-body)",
    fontWeight: 600,
    fontSize: 14,
    cursor: "pointer",
    transition: "background 0.15s, color 0.15s",
  };

  return (
    <div
      role="tablist"
      style={{
        display: "flex",
        background: "var(--surface-sunk)",
        borderRadius: "var(--r-lg)",
        padding: 4,
        gap: 4,
        marginBottom: 28,
      }}
    >
      {(["have", "share"] as LinkTab[]).map((t) => {
        const active = tab === t;
        return (
          <button
            key={t}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t)}
            style={{
              ...base,
              background: active ? "var(--surface)" : "transparent",
              color: active ? "var(--ink-deep)" : "var(--ink-soft)",
              borderRadius: "calc(var(--r-lg) - 2px)",
              boxShadow: active ? "0 1px 3px rgba(0,0,0,.08)" : "none",
            }}
          >
            {t === "have" ? haveLabel : shareLabel}
          </button>
        );
      })}
    </div>
  );
}

// ---- Enter code (have) tab ---------------------------------------------------

function HaveTab({ dict }: { dict: ReturnType<typeof getDictionary> }) {
  const t = dict.service.link;
  const live = shouldUseSupabase();
  const [input, setInput] = useState("");
  const [claimState, setClaimState] = useState<ClaimState>("entered");
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const normalized = normalizeLinkCode(input);
  const isValid = isValidLinkCode(normalized);
  const preview = isValid ? formatLinkCode(normalized) : null;

  async function handleSubmit() {
    if (!isValid || submitted || busy) return;
    setError(null);
    if (!live) {
      // Demo path: local claim state machine (no backend).
      setClaimState(claimTransition(claimState, "submit"));
      setSubmitted(true);
      return;
    }
    // Live path: claim the code against the shared backend — the SAME
    // claim_link_code RPC the mobile app calls, so a code minted in the app
    // links here (and vice-versa).
    setBusy(true);
    try {
      await getPortalData().claimLinkCode(normalized);
      setClaimState("pendingOwnerConsent");
      setSubmitted(true);
    } catch (e) {
      setError(claimErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  if (submitted && claimState === "pendingOwnerConsent") {
    return (
      <div
        style={{
          background: "var(--peach-tint)",
          border: "1px solid var(--peach)",
          borderRadius: "var(--r-lg)",
          padding: "24px 22px",
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <h2
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            fontSize: 20,
            color: "var(--peach-deep)",
            margin: 0,
          }}
        >
          Awaiting consent
        </h2>
        <p style={{ color: "var(--peach-deep)", fontSize: 15.5, margin: 0, lineHeight: 1.6 }}>
          The owner will need to confirm before you can see engagement summaries.
          You'll see this child in your list once they do.
        </p>
        <p style={{ color: "var(--ink-faint)", fontSize: 13, margin: 0 }}>
          Code: <strong>{formatLinkCode(normalized)}</strong>
        </p>
        <button
          onClick={() => {
            setInput("");
            setClaimState("entered");
            setSubmitted(false);
          }}
          style={{
            alignSelf: "flex-start",
            marginTop: 4,
            minHeight: 44,
            padding: "0 18px",
            border: "1.5px solid var(--peach-deep)",
            background: "transparent",
            color: "var(--peach-deep)",
            fontFamily: "var(--font-body)",
            fontWeight: 600,
            fontSize: 14,
            borderRadius: "var(--r-pill)",
            cursor: "pointer",
          }}
        >
          Enter another code
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <p style={{ color: "var(--ink-soft)", fontSize: 15.5, margin: 0, lineHeight: 1.6 }}>
        Ask the person who looks after the child to share their link code with
        you. Enter it below — the owner will confirm before you can see
        summaries.
      </p>

      <div>
        <label
          htmlFor="link-code-input"
          style={{
            display: "block",
            fontWeight: 600,
            fontSize: 14,
            color: "var(--ink)",
            marginBottom: 8,
          }}
        >
          {t.codeLabel}
        </label>
        <input
          id="link-code-input"
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="e.g. ABCD-EF23"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          maxLength={12}
          style={{
            width: "100%",
            maxWidth: 280,
            minHeight: 48,
            padding: "0 16px",
            border: "1.5px solid var(--line)",
            borderRadius: "var(--r-lg)",
            fontFamily: "var(--font-mono, monospace)",
            fontSize: 20,
            fontWeight: 700,
            letterSpacing: ".12em",
            color: "var(--ink-deep)",
            background: "var(--surface)",
            boxSizing: "border-box",
            outline: "none",
          }}
          onFocus={(e) => (e.currentTarget.style.borderColor = "var(--brand)")}
          onBlur={(e) => (e.currentTarget.style.borderColor = "var(--line)")}
        />
        {preview && (
          <p style={{ fontSize: 13, color: "var(--ink-faint)", marginTop: 6 }}>
            Formatted: <strong style={{ fontFamily: "monospace", letterSpacing: ".08em" }}>{preview}</strong>
          </p>
        )}
      </div>

      {error && (
        <div
          role="alert"
          style={{
            background: "var(--peach-tint)",
            border: "1px solid var(--peach)",
            borderRadius: "var(--r-md)",
            padding: "12px 16px",
            color: "var(--peach-deep)",
            fontSize: 14.5,
            lineHeight: 1.5,
          }}
        >
          {error}
        </div>
      )}

      <button
        onClick={() => void handleSubmit()}
        disabled={!isValid || busy}
        style={{
          alignSelf: "flex-start",
          minHeight: 48,
          padding: "0 24px",
          border: 0,
          background: isValid && !busy ? "var(--brand)" : "var(--surface-sunk)",
          color: isValid && !busy ? "#fff" : "var(--ink-faint)",
          fontFamily: "var(--font-body)",
          fontWeight: 600,
          fontSize: 15,
          borderRadius: "var(--r-pill)",
          cursor: isValid && !busy ? "pointer" : "not-allowed",
        }}
      >
        {busy ? dict.common.sending : t.linkButton}
      </button>

      <p style={{ fontSize: 13, color: "var(--ink-faint)", margin: 0 }}>
        Nothing is shared until the owner confirms. You can link to the same
        child as other adults — one child, multiple carers.
      </p>
    </div>
  );
}

// ---- Share code (share) tab --------------------------------------------------

/** Live: pick an owned child, mint a real single-use code via generate_link_code. */
function ShareTabLive({ dict: _dict }: { dict: ReturnType<typeof getDictionary> }) {
  const ctx = useOutletContext<ServiceOutletContext | null>();
  const userId = ctx?.userId;
  const [children, setChildren] = useState<ServerChild[] | null>(null);
  const [code, setCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    getPortalData()
      .children()
      .then((all) => {
        if (alive) setChildren(all.filter((c) => c.ownerAccountId === userId));
      })
      .catch(() => {
        if (alive) setChildren([]);
      });
    return () => {
      alive = false;
    };
  }, [userId]);

  async function handleGenerate(childId: string) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const raw = await getPortalData().generateLinkCode(childId, ["progress"]);
      setCode(formatLinkCode(raw));
    } catch (e) {
      setError(
        e instanceof PortalWriteError && e.code === "42501"
          ? "Only the child's owner can create a link code."
          : "Couldn't create a code just now. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  function handleCopy() {
    if (!code) return;
    navigator.clipboard
      .writeText(code)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {});
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <p style={{ color: "var(--ink-soft)", fontSize: 15.5, margin: 0, lineHeight: 1.6 }}>
        Choose a child you look after, and we'll create a one-time code to share
        with another adult. They enter it on their side, and you confirm before
        they see anything.
      </p>

      {children === null ? (
        <p style={{ color: "var(--ink-faint)", fontSize: 14, margin: 0 }}>
          Loading your children…
        </p>
      ) : children.length === 0 ? (
        <p style={{ color: "var(--ink-soft)", fontSize: 14.5, margin: 0, lineHeight: 1.6 }}>
          You don't own any children yet. Add a child in the Zooylo app first,
          then come back to share a code.
        </p>
      ) : code ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 14 }}>
          <div
            aria-label={`Link code: ${code}`}
            style={{ background: "var(--surface)", border: "2px solid var(--brand)", borderRadius: "var(--r-lg)", padding: "18px 28px", display: "inline-block" }}
          >
            <span style={{ fontFamily: "var(--font-mono, monospace)", fontSize: 32, fontWeight: 700, letterSpacing: ".18em", color: "var(--brand)", userSelect: "all" }}>
              {code}
            </span>
          </div>
          <button
            onClick={handleCopy}
            style={{ minHeight: 44, padding: "0 20px", border: "1.5px solid var(--brand)", background: copied ? "var(--brand)" : "transparent", color: copied ? "#fff" : "var(--brand)", fontFamily: "var(--font-body)", fontWeight: 600, fontSize: 14, borderRadius: "var(--r-pill)", cursor: "pointer", transition: "background 0.15s, color 0.15s" }}
          >
            {copied ? "Copied!" : "Copy code"}
          </button>
          <button
            onClick={() => setCode(null)}
            style={{ background: "transparent", border: 0, color: "var(--ink-soft)", fontFamily: "var(--font-body)", fontSize: 13.5, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}
          >
            Create another code
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <span style={{ fontWeight: 600, fontSize: 14, color: "var(--ink)" }}>Pick a child</span>
          {children.map((c) => (
            <button
              key={c.id}
              onClick={() => void handleGenerate(c.id)}
              disabled={busy}
              style={{ textAlign: "start", minHeight: 48, padding: "0 16px", border: "1.5px solid var(--line)", borderRadius: "var(--r-lg)", background: "var(--surface)", color: "var(--ink-deep)", fontFamily: "var(--font-body)", fontWeight: 600, fontSize: 14.5, cursor: busy ? "wait" : "pointer" }}
            >
              Child ••{c.id.slice(-4)}
            </button>
          ))}
        </div>
      )}

      {error && (
        <div role="alert" style={{ background: "var(--peach-tint)", border: "1px solid var(--peach)", borderRadius: "var(--r-md)", padding: "12px 16px", color: "var(--peach-deep)", fontSize: 14.5, lineHeight: 1.5 }}>
          {error}
        </div>
      )}
    </div>
  );
}

/** Dispatcher: real generate flow when Supabase is configured, demo code otherwise. */
function ShareTab({ dict }: { dict: ReturnType<typeof getDictionary> }) {
  return shouldUseSupabase() ? (
    <ShareTabLive dict={dict} />
  ) : (
    <ShareTabDemo dict={dict} />
  );
}

function ShareTabDemo({ dict: _dict }: { dict: ReturnType<typeof getDictionary> }) {
  const [sharedCode] = useState(() => generateLinkCode());
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(sharedCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      // clipboard unavailable (non-HTTPS) — silently ignore
    });
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <p style={{ color: "var(--ink-soft)", fontSize: 15.5, margin: 0, lineHeight: 1.6 }}>
        Share this code with another adult — a co-parent, a centre worker, or
        anyone else you trust. They enter it on their side, and you'll get a
        request to confirm before they see anything.
      </p>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 14,
        }}
      >
        <div
          aria-label={`Link code: ${sharedCode}`}
          style={{
            background: "var(--surface)",
            border: "2px solid var(--brand)",
            borderRadius: "var(--r-lg)",
            padding: "18px 28px",
            display: "inline-block",
          }}
        >
          <span
            style={{
              fontFamily: "var(--font-mono, monospace)",
              fontSize: 32,
              fontWeight: 700,
              letterSpacing: ".18em",
              color: "var(--brand)",
              userSelect: "all",
            }}
          >
            {sharedCode}
          </span>
        </div>

        <button
          onClick={handleCopy}
          style={{
            minHeight: 44,
            padding: "0 20px",
            border: "1.5px solid var(--brand)",
            background: copied ? "var(--brand)" : "transparent",
            color: copied ? "#fff" : "var(--brand)",
            fontFamily: "var(--font-body)",
            fontWeight: 600,
            fontSize: 14,
            borderRadius: "var(--r-pill)",
            cursor: "pointer",
            transition: "background 0.15s, color 0.15s",
          }}
        >
          {copied ? "Copied!" : "Copy code"}
        </button>
      </div>

      <div
        style={{
          background: "var(--surface-sunk)",
          border: "1px solid var(--line)",
          borderRadius: "var(--r-lg)",
          padding: "16px 18px",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <p style={{ fontSize: 13.5, color: "var(--ink-soft)", margin: 0, fontWeight: 600 }}>
          How it works
        </p>
        <ul
          style={{
            margin: 0,
            paddingLeft: 18,
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          {[
            "They enter the code on their Zooylo portal.",
            "You'll get a request to confirm — you control what they can see.",
            "Multiple adults can link to the same child.",
            "You can remove access at any time in Sharing & consent.",
          ].map((text) => (
            <li key={text} style={{ fontSize: 13.5, color: "var(--ink-soft)", lineHeight: 1.5 }}>
              {text}
            </li>
          ))}
        </ul>
      </div>

      <p style={{ fontSize: 13, color: "var(--ink-faint)", margin: 0 }}>
        This code is safe to share — nothing is visible until you confirm
        consent.
      </p>
    </div>
  );
}

// ---- Main export -------------------------------------------------------------

export function LinkAChild() {
  const { locale } = useComfort();
  const dict = getDictionary(locale);
  const t = dict.service.link;

  const [tab, setTab] = useState<LinkTab>("have");

  return (
    <div>
      <h1
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: 28,
          color: "var(--ink-deep)",
          margin: "0 0 6px",
        }}
      >
        {t.title}
      </h1>
      <p style={{ color: "var(--ink-soft)", fontSize: 15.5, margin: "0 0 28px", lineHeight: 1.6 }}>
        {t.subtitle}
      </p>

      <TabBar
        tab={tab}
        haveLabel={t.haveTab}
        shareLabel={t.shareTab}
        onChange={(next) => setTab(next)}
      />

      {tab === "have" ? (
        <HaveTab dict={dict} />
      ) : (
        <ShareTab dict={dict} />
      )}
    </div>
  );
}
